import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {FieldType, MemberAddeditComponent, MemberAddeditDialogModel} from '../../brokeragency/member-addedit/member-addedit.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import {
  VendorProductsService,
  VendorProductTransferLog,
} from '../../../services/vendorproducts/vendorproducts.service';


@Component({
    selector: 'app-vendor-pending-transfers-page',
    templateUrl: './vendor-pending-transfers-page.component.html',
    styleUrls: ['./vendor-pending-transfers-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VendorPendingTransfersPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('scrollContainer') private scrollContainer?: ElementRef<HTMLElement>;

  public limit = 25;
  public page = 0;
  public searchValue = '';
  public endOfList = false;
  public loading = false;

  protected allColumnNames = ['createdAt', 'vendorProductName',  'metadata', 'data', 'status', 'exportHealth', 'actionmenu'];
  protected items = new MatTableDataSource<any>();

  private readonly searchDebounceMs = 300;
  private readonly searchValueChanges = new Subject<string>();
  private readonly searchSubscription: Subscription;
  private addEditFields = [
    {name: 'createdAt', label: 'Created at', fieldType: FieldType.F_STRING, readOnly: true},
    {name: 'vendorProductName', label: 'Vendor product name', fieldType: FieldType.F_STRING, readOnly: true},
    {name: 'status', label: 'Status', fieldType: FieldType.F_DROPDOWN, values: ['new', 'sent', 'resend']},
    {name: 'metadata', label: 'Metadata JSON', fieldType: FieldType.F_JSON},
    {name: 'data', label: 'Data JSON', fieldType: FieldType.F_JSON},
  ];

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private spinnerService: SpinnerService,
    private uiAlertService: UiAlertService,
    private vendorProductsService: VendorProductsService,
  ) {
    this.searchSubscription = this.searchValueChanges
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
      )
      .subscribe(() => this.searchItems());
  }

  ngOnInit(): void {
    this.searchItems();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.loadMoreIfViewportNotFilled());
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  public cycleStatus(item: VendorProductTransferLog): void {
    const statuses = ['new', 'sent', 'resend'];
    let index = statuses.findIndex(status => status === item.status);
    index = ++index % statuses.length;
    item.status = statuses[index];
    this.vendorProductsService.putVendorPendingTransferLogItem(item).then(async () => {
      await this.reloadItems();
    });
  }

  deleteItem(item: VendorProductTransferLog): void {
    this.uiAlertService.presentAlertConfirm('Are you sure you want to delete this vendor pending transfer?').then(async confirm => {
      if (confirm) {
        if (!item?.id) {
          this.snackBar.open('Unable to delete vendor pending transfer: missing id', 'Close', {
            duration: 3000
          });
          return;
        }
        this.spinnerService.show('Deleting vendor pending transfer...');
        this.vendorProductsService.deleteVendorPendingTransferLog(item.id).then(() => {
          this.reloadItems();
          this.spinnerService.hide();
          this.snackBar.open('Vendor pending transfer deleted', 'Close', {
            duration: 3000
          });

        }).catch(() => {
          this.spinnerService.hide();
          this.snackBar.open('Unable to delete vendor pending transfer', 'Close', {
            duration: 3000
          });
        });
      }
    });
  }

  async searchItems(): Promise<void> {
    await this.loadItems(true);
  }

  queueFilter(value: string): void {
    this.searchValueChanges.next((value || '').toString());
  }

  onScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;

    if (scrollTop + offsetHeight >= scrollHeight - 150) {
      this.loadItems();
    }
  }

  async clearSearch(): Promise<void> {
    this.searchValue = '';
    this.queueFilter('');
  }

  stringify(jsonObject: Record<string, any>): string {
    return JSON.stringify(jsonObject);
  }

  editItem(item: VendorProductTransferLog): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(item, this.addEditFields, 'Vendor pending transfer')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (!dialogResult) {
        return;
      }

      const updatedItem: VendorProductTransferLog = {
        ...item,
        ...dialogResult,
      };

      this.vendorProductsService.putVendorPendingTransferLogItem(updatedItem).then(async () => {
        this.snackBar.open('Vendor pending transfer updated', 'Close', {
          duration: 3000
        });
        await this.reloadItems();
      }).catch(() => {
        this.snackBar.open('Unable to update vendor pending transfer', 'Close', {
          duration: 3000
        });
      });
    });
  }

  private async loadItems(clear = false): Promise<void> {
    if (clear) {
      this.page = 0;
      this.endOfList = false;
      this.items.data = [];
    }

    if (this.loading || this.endOfList) {
      return;
    }

    this.loading = true;
    try {
      const vendorPendingTransferLogs = await this.vendorProductsService.getVendorPendingTransferLogs(
        this.searchValue,
        this.page,
        this.limit,
      );

      if (!vendorPendingTransferLogs || vendorPendingTransferLogs.length === 0) {
        this.endOfList = true;
        return;
      }

      this.items.data = clear
        ? vendorPendingTransferLogs
        : this.items.data.concat(vendorPendingTransferLogs);
      this.page += 1;
      this.endOfList = vendorPendingTransferLogs.length < this.limit;
      this.items.sort = this.sort;
    } finally {
      this.loading = false;
      this.loadMoreIfViewportNotFilled();
    }
  }

  private async reloadItems(): Promise<void> {
    const loadedItemCount = this.items.data.length || this.limit;
    const pagesToReload = Math.max(1, Math.ceil(loadedItemCount / this.limit));

    this.loading = true;
    this.page = 0;
    this.endOfList = false;

    try {
      const reloadedItems: VendorProductTransferLog[] = [];

      for (let page = 0; page < pagesToReload; page += 1) {
        const vendorPendingTransferLogs = await this.vendorProductsService.getVendorPendingTransferLogs(
          this.searchValue,
          page,
          this.limit,
        );

        reloadedItems.push(...vendorPendingTransferLogs);

        if (vendorPendingTransferLogs.length < this.limit) {
          this.endOfList = true;
          break;
        }
      }

      this.items.data = reloadedItems;
      this.page = Math.ceil(reloadedItems.length / this.limit);
      this.endOfList = this.endOfList || reloadedItems.length === 0;
      this.items.sort = this.sort;
    } finally {
      this.loading = false;
      this.loadMoreIfViewportNotFilled();
    }
  }

  private loadMoreIfViewportNotFilled(): void {
    if (this.loading || this.endOfList) {
      return;
    }

    const container = this.scrollContainer?.nativeElement;
    if (!container) {
      return;
    }

    const hasVerticalScroll = container.scrollHeight > container.clientHeight;
    const nearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 150;

    if (!hasVerticalScroll || nearBottom) {
      this.loadItems();
    }
  }
}
